import { createAction } from '@reduxjs/toolkit'
import { DialogOptionActionManifestJson, DialogOptionManifestJson } from '../../API/DialogJson'
import AudioController from '../../Controllers/AudioController'
import dispatch from '../../Utility/dispatchAction'
import DialogPart from './DialogPart'

export const dialogOptionActionType = 'DialogOption.choose'

export const DialogOptionActionCreator = createAction(
    dialogOptionActionType,
    (dialogOption: DialogOption, action?: DialogOptionActionManifestJson) => ({
        payload: {
            dialogOption,
            action,
        },
    })
)

/**
 * Use this to listen for interaction with a dialog option
 * @param callback
 * @param once
 * @returns
 */
export const createDialogOptionActionListener = (
    callback?: (dialogOption: DialogOption) => void,
    once = false
): Promise<unknown> =>
    new Promise<void>((resolve) => {
        const handler: EventListener = (event) => {
            // @ts-ignore -- WHY: CustomEvent
            const dialogOption = event.detail.dialogOption as DialogOption

            once && removeEventListener(dialogOptionActionType, handler)
            callback && callback(dialogOption)
            resolve()
        }

        addEventListener(dialogOptionActionType, handler)
    })

/**
 * Use this to listen for interaction with a dialog option with event id
 * @param eventId The event id to listen for
 * @param callback
 * @param once
 * @returns
 */
export const createDialogOptionEventListener = (
    eventId: string,
    callback?: (dialogOption: DialogOption) => void,
    once = false
): Promise<unknown> =>
    new Promise<void>((resolve) => {
        const handler: EventListener = (event) => {
            // @ts-ignore -- WHY: CustomEvent
            const dialogOption = event.detail.dialogOption as DialogOption

            if (eventId === dialogOption.action?.eventId) {
                once && removeEventListener(dialogOptionActionType, handler)
                callback && callback(dialogOption)
                resolve()
            }
        }

        addEventListener(dialogOptionActionType, handler)
    })

export type DialogOptionClickHandler = (option: DialogOption) => Promise<void>

export default class DialogOption {
    id: number
    element = document.createElement('button')
    text = document.createElement('span')
    responseId: string
    subtitles: string
    available: boolean
    audioId?: string
    nextDialogPartId: string
    action?: DialogOptionActionManifestJson
    clickHandler?: DialogOptionClickHandler
    part: DialogPart

    constructor(id: number, manifest: DialogOptionManifestJson, part: DialogPart) {
        this.id = id
        this.part = part
        this.available = manifest.available
        this.action = manifest.action

        if (!this.available) {
            this.hide()
        }

        this.subtitles = manifest.subtitles
        this.audioId = manifest.audio ? AudioController.add(manifest.audio) : undefined
        this.responseId = manifest.response

        this.element.type = 'button'
        this.element.classList.add('dialog-option')

        this.text.innerText = manifest.text
        this.text.classList.add('dialog-option-text')
        this.element.appendChild(this.text)

        this.nextDialogPartId = manifest.nextPart
        this.element.onclick = this.handleClick
    }

    setClickHandler = (handler: DialogOptionClickHandler) => {
        this.clickHandler = handler
    }

    handleClick = async () => {
        if (this.clickHandler) {
            await this.clickHandler(this)
        }

        this.markAsAlreadyChosen()

        dispatch(DialogOptionActionCreator(this, this.action))
    }

    show = () => {
        this.element.classList.remove('hidden')
    }

    hide = () => {
        this.element.classList.add('hidden')
    }

    setAvailable = (value: boolean) => {
        this.available = value
    }

    markAsAlreadyChosen = () => {
        this.element.classList.add('visited')
    }

    destroy = () => {
        if (this.audioId) {
            AudioController.remove(this.audioId)
        }

        this.text.remove()
        this.element.remove()
    }
}
